import { DateFormat } from '@equip.health/ui';
import { DateTime } from 'luxon';

import { BillingPeriodCoverageValues } from '~/components/common/billingPeriods/types';

import { formattedDateFromISO } from './date.util';
import { OTHER_PAYOR } from '../constants/inquiry.constants';

export const BillingPeriodAuthorizationStatusMapper: Record<
  BillingPeriodAuthorizationStatus,
  'pending' | 'approved' | 'denied'
> = {
  Approved: 'approved',
  Denied: 'denied',
  Pending: 'pending',
};

export const getBillingPeriodDateRange = (
  startDate: string,
  endDate: string,
): string => {
  if (!startDate && !endDate) {
    return undefined;
  }
  const { shortMonthDayYear } = DateFormat;
  const formattedStartDate = formattedDateFromISO(startDate, shortMonthDayYear);
  if (!endDate) {
    return `Starts ${formattedStartDate}`;
  }

  const formattedEndDate = formattedDateFromISO(endDate, shortMonthDayYear);
  if (!startDate) {
    return `Ends ${formattedEndDate}`;
  }

  return `${formattedStartDate} - ${formattedEndDate}`;
};

export const isBillingPeriodActive = (
  startDateStr: string,
  endDateStr: string | undefined,
): boolean => {
  const currentDate = DateTime.now().toJSDate();
  const startDate = startDateStr ? new Date(startDateStr) : null;
  const endDate = endDateStr ? new Date(endDateStr) : null;

  if (!endDate && !startDate) {
    return false;
  }

  if (!endDate) {
    return currentDate >= startDate; // If end date is undefined, return true (billing period is always active after start date)
  }

  endDate.setUTCDate(endDate.getUTCDate() + 1); // Add one day to include the entire end date

  return currentDate >= startDate && currentDate < endDate;
};

export const getMaxAuthorizationEndDate = (
  billingPeriods: BillingPeriod[],
): string | undefined => {
  const approvedAuthorizationEndDates = billingPeriods
    .flatMap((billingPeriod) => billingPeriod.authorizationDetails ?? [])
    .filter(
      (
        authorization,
      ): authorization is typeof authorization & { endDate: string } =>
        authorization.status === 'Approved' &&
        !authorization.isDeleted &&
        typeof authorization.endDate === 'string' &&
        authorization.endDate.length > 0,
    )
    .map((authorization) => authorization.endDate);

  if (!approvedAuthorizationEndDates.length) {
    return undefined;
  }

  return approvedAuthorizationEndDates.reduce((maxDate, nextDate) => {
    return DateTime.fromISO(nextDate).toMillis() >
      DateTime.fromISO(maxDate).toMillis()
      ? nextDate
      : maxDate;
  }, approvedAuthorizationEndDates[0]);
};

export const getInsuranceAuthorizationEndDate = (
  billingPeriods: BillingPeriod[],
  medicalCoverage: {
    coverageExternalId?: string;
    coveragePriority?: number;
    isActive?: boolean;
  }[],
): string | undefined => {
  const primaryCoverages = (medicalCoverage ?? []).filter(
    (coverage) => coverage.coveragePriority === 1 && coverage.isActive,
  );

  if (primaryCoverages.length !== 1) {
    return undefined;
  }

  const primaryCoverageExternalId = primaryCoverages[0].coverageExternalId;
  const activeBillingPeriodsConnectedToPrimaryCoverage = (
    billingPeriods ?? []
  ).filter(
    (billingPeriod) =>
      billingPeriod.coverageExternalId === primaryCoverageExternalId &&
      isBillingPeriodActive(billingPeriod.startDate, billingPeriod.endDate),
  );

  if (!activeBillingPeriodsConnectedToPrimaryCoverage.length) {
    return undefined;
  }

  return getMaxAuthorizationEndDate(
    activeBillingPeriodsConnectedToPrimaryCoverage,
  );
};

export const extractExternalIds = (
  data: {
    label: string;
    externalId: string;
    isActive?: boolean;
    isInNetwork?: boolean;
    coverageExternalId?: string;
  }[],
  label: string,
) => {
  const expectedItem = data?.find((obj) => {
    return obj.label === label;
  });
  return {
    coverageExternalId: expectedItem?.coverageExternalId,
    externalId: expectedItem?.externalId,
  };
};

export const getTreatmentEpisodeDescription = (
  treatmentProgramName: string,
  episodeStartDate?: string,
): string | null => {
  return treatmentProgramName
    ? `${treatmentProgramName}, ${episodeStartDate}`
    : null;
};

export const getTreatmentEpisodeName = (
  obj: BillingPeriod | AdminEpisodesProps,
): string | null =>
  getTreatmentEpisodeDescription(
    obj?.treatmentProgramName,
    obj?.episodeStartDate,
  );

export const getBillingPeriodCoverageInfo = (billingPeriod: BillingPeriod) => {
  const billingPeriodCoverageInfo: BillingPeriodCoverageValues = {
    endDate: billingPeriod?.endDate,
    episodeStartDate: billingPeriod?.episodeStartDate,
    equipPayor: billingPeriod?.equipPayorName,
    equipPayorId: billingPeriod?.equipPayorExternalId,
    isAuthRequired: billingPeriod?.isAuthRequired,
    payor:
      billingPeriod?.payorName === OTHER_PAYOR
        ? billingPeriod?.customPayorName
        : billingPeriod?.payorName,
    payorExternalId: billingPeriod?.payorExternalId,
    payorProgramName: billingPeriod?.payorProgramName,
    startDate: billingPeriod?.startDate,
    treatmentEpisode: getTreatmentEpisodeName(billingPeriod),
  };
  return billingPeriodCoverageInfo;
};
